// 📁 src/components/MicButton.jsx
// 🎤 Botón de Micrófono para STT (Speech-to-Text)

import React, { useState, useEffect } from 'react';
import { Mic, MicOff, Loader2, AlertCircle, X } from 'lucide-react';
import { speechToText } from '../services/voiceApiService';
import { audioRecorder } from '../utils/audioRecorder';

/**
 * Botón de micrófono para grabar y transcribir audio
 * Estados: idle → requesting-permission → recording → transcribing → success/error
 * 
 * @param {Function} onTranscriptionComplete - Callback con el texto transcrito
 * @param {boolean} disabled - Deshabilitar el botón
 * @param {string} className - Clases CSS adicionales
 */
const MicButton = ({ onTranscriptionComplete, disabled = false, className = '' }) => {
  const [state, setState] = useState('idle'); // idle | requesting-permission | recording | transcribing | error
  const [error, setError] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  /**
   * Maneja el click en el botón según el estado
   */
  const handleClick = async () => {
    if (disabled) return;

    switch (state) {
      case 'idle':
        await startRecording();
        break;
      case 'recording':
        await stopRecording();
        break;
      case 'error':
        // Reset al hacer click en error
        setState('idle');
        setError(null);
        break;
      default:
        break;
    }
  };

  /**
   * Cancela la grabación actual
   */
  const handleCancel = () => {
    if (state === 'recording') {
      audioRecorder.cancel();
      if (timerInterval) {
        clearInterval(timerInterval);
      }
      setState('idle');
      setRecordingTime(0);
      setError(null);
    }
  };

  /**
   * Inicia la grabación de audio
   */
  const startRecording = async () => {
    setState('requesting-permission');
    setError(null);

    try {
      // Solicitar permisos primero
      await audioRecorder.requestPermissions();

      // Iniciar grabación
      await audioRecorder.start();
      setState('recording');
      setRecordingTime(0);

      // Iniciar timer
      const interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);

    } catch (err) {
      console.error('[MicButton] Error al iniciar grabación:', err);
      setState('error');
      setError(err.message || 'No se pudo acceder al micrófono');
      
      // Volver a idle después de 5 segundos
      setTimeout(() => {
        if (state === 'error') {
          setState('idle');
          setError(null);
        }
      }, 5000);
    }
  };

  /**
   * Detiene la grabación y transcribe el audio
   */
  const stopRecording = async () => {
    setState('transcribing');
    
    // Detener timer
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    try {
      // Obtener audio grabado
      const { blob, mimeType, durationMs } = await audioRecorder.stop();

      console.log('[MicButton] Audio grabado:', {
        size: `${(blob.size / 1024).toFixed(2)} KB`,
        duration: `${(durationMs / 1000).toFixed(1)}s`,
        type: mimeType
      });

      // Verificar duración mínima (al menos 0.5 segundos)
      if (durationMs < 500) {
        throw new Error('Audio demasiado corto. Intenta grabar por más tiempo.');
      }

      // Crear FormData para enviar al backend
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      formData.append('language', 'es');

      // Transcribir
      const { text } = await speechToText(formData);

      console.log('[MicButton] Texto transcrito:', text);

      // Verificar que hay texto
      if (!text || text.trim() === '') {
        throw new Error('No se detectó voz en la grabación. Intenta hablar más cerca del micrófono.');
      }

      // Notificar transcripción exitosa
      if (onTranscriptionComplete) {
        onTranscriptionComplete(text);
      }

      // Volver a idle
      setState('idle');
      setRecordingTime(0);

    } catch (err) {
      console.error('[MicButton] Error al transcribir:', err);
      setState('error');
      setError(err.message || 'No se pudo transcribir el audio');
      setRecordingTime(0);

      // Volver a idle después de 5 segundos
      setTimeout(() => {
        if (state === 'error') {
          setState('idle');
          setError(null);
        }
      }, 5000);
    }
  };

  /**
   * Formatea el tiempo de grabación
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Obtiene el contenido del botón según el estado
   */
  const getButtonContent = () => {
    switch (state) {
      case 'requesting-permission':
        return (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-xs hidden md:inline">Solicitando permisos...</span>
          </>
        );
      case 'recording':
        return (
          <>
            <Mic className="w-5 h-5 animate-pulse" />
            <span className="text-xs font-mono hidden md:inline">{formatTime(recordingTime)}</span>
          </>
        );
      case 'transcribing':
        return (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-xs hidden md:inline">Transcribiendo...</span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-5 h-5" />
            <span className="text-xs hidden md:inline">Reintentar</span>
          </>
        );
      default:
        return (
          <>
            <Mic className="w-5 h-5" />
            <span className="text-xs hidden md:inline">Hablar</span>
          </>
        );
    }
  };

  /**
   * Obtiene las clases CSS del botón según el estado - Modernizado
   */
  const getButtonClasses = () => {
    const baseClasses = `inline-flex items-center gap-2 px-4 py-3 md:px-4 md:py-3.5 
      rounded-xl md:rounded-2xl font-medium transition-all duration-200 ease-out
      shadow-sm hover:shadow-md
      min-h-[52px] md:min-h-[64px]
      ${className}`;
    
    switch (state) {
      case 'requesting-permission':
        return `${baseClasses} bg-gray-100 text-gray-600 cursor-wait`;
      case 'recording':
        return `${baseClasses} bg-gradient-to-br from-red-500 to-red-600 text-white 
          hover:from-red-600 hover:to-red-700 hover:scale-105 shadow-lg`;
      case 'transcribing':
        return `${baseClasses} bg-indigo-100 text-indigo-700 cursor-wait`;
      case 'error':
        return `${baseClasses} bg-red-50 text-red-700 border border-red-200 hover:bg-red-100`;
      default:
        return `${baseClasses} bg-gradient-to-br from-indigo-50 to-indigo-100 
          text-indigo-700 hover:from-indigo-100 hover:to-indigo-200 
          hover:scale-105 active:scale-95
          border border-indigo-200/50
          focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2
          ${disabled ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`;
    }
  };

  return (
    <div className="relative inline-flex items-center gap-2">
      {/* Botón principal */}
      <button
        onClick={handleClick}
        disabled={disabled || state === 'requesting-permission' || state === 'transcribing'}
        className={getButtonClasses()}
        title={error || (state === 'recording' ? 'Click para detener' : 'Hablar por micrófono')}
      >
        {getButtonContent()}
      </button>

      {/* Botón de cancelar (solo cuando está grabando) */}
      {state === 'recording' && (
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-1 px-2 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
          title="Cancelar grabación"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Indicador de grabación (visual adicional) */}
      {state === 'recording' && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
      )}

      {/* Mensaje de error */}
      {error && state === 'error' && (
        <div className="absolute top-full mt-2 left-0 right-0 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 shadow-lg z-10 whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
};

export default MicButton;
